/* ==========================================================================
   THE OPEN FAUCET — PRODUCTION configuration (Base mainnet)
   --------------------------------------------------------------------------
   The ONE place where deployment-specific values live.

   Nothing here is invented. Both contract addresses default to the explicit
   zero address; until the real Base MAINNET addresses are supplied via
   `.env.local`, `contractsConfigured` is false and the UI:

     - keeps the full layout,
     - shows "Production contract configuration pending.",
     - disables every approval and draw transaction,
     - shows "—" instead of any number it cannot read from chain.

   THE CONTRACT IS THE ONLY PRICING AUTHORITY. This file carries no price, no
   tier table and no schedule. `quote(quantity)` is read from the Faucet for
   every figure shown and for the `maxCbBTC` bound actually signed, so the page
   cannot disagree with what a draw will cost. That is the whole reason the
   contract exposes a quote function.
   ========================================================================== */

import { base } from 'viem/chains'
import { isAddress, zeroAddress, type Address } from 'viem'

/** The only network this build talks to. Not configurable. */
export const CHAIN = base
export const CHAIN_ID = base.id // 8453
export const EXPLORER_URL = base.blockExplorers.default.url // https://basescan.org

export const ZERO_ADDRESS = zeroAddress

/**
 * Reads an address from the environment.
 *
 * A missing, blank, malformed or zero value all resolve to the zero address —
 * a deliberate, visible placeholder. We never substitute a guess.
 */
function envAddress(raw: string | undefined): Address {
  const value = (raw ?? '').trim()
  if (!value) return ZERO_ADDRESS
  if (!isAddress(value, { strict: false })) {
    console.warn(
      `[open-faucet] Ignoring malformed address in environment: ${JSON.stringify(value)}. ` +
        'Falling back to the zero address placeholder.',
    )
    return ZERO_ADDRESS
  }
  return value as Address
}

/**
 * Deployment addresses.
 *
 * `faucet` and `seeds` come from the production manifest and are zero until
 * supplied. `cbBTC` and `taboshi1` are LIVE Base mainnet assets that predate
 * this deployment and are verified on chain, so they are hard-coded rather
 * than left configurable — a typo in an env file must not be able to point the
 * approval flow at a different token.
 */
export const ADDRESSES = {
  /** THE OPEN FAUCET — the source of truth for pricing, state and draws. */
  faucet: envAddress(import.meta.env.VITE_FAUCET_ADDRESS),
  /** Taboshi Seeds ERC-1155 — SEED balances and receipt logs. */
  seeds: envAddress(import.meta.env.VITE_SEEDS_ADDRESS),
  /**
   * cbBTC on Base. VERIFIED ON CHAIN: symbol "cbBTC", decimals 8.
   * 8 decimals means the base unit IS the satoshi, which is why every amount in
   * this app is an integer satoshi count and no decimal scaling appears anywhere.
   */
  cbBTC: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf' as Address,
  /**
   * Legacy taboshi1 ERC-1155. VERIFIED ON CHAIN: Zora Creator 1155 v2.13.2
   * behind a proxy. An enhanced draw retires tokenId 1 from the participant's
   * own balance, which requires `setApprovalForAll` on THIS contract.
   */
  taboshi1: '0x5C0BF08936bcCfbb6af24B4648A9fb365cAa2F4e' as Address,
  /**
   * SIP ROUTER — DORMANT. Deliberately NOT in REQUIRED_ADDRESSES: while this is
   * the zero address the page renders EXACTLY as it does in production today —
   * no third route, no Router reads, no new copy. The TAKE A SIP option appears
   * only when a deployed Router address is wired in at the future manual launch.
   */
  sipRouter: envAddress(import.meta.env.VITE_SIP_ROUTER_ADDRESS),
} as const

/** True only when the dormant Sip route has been deliberately activated. */
export const sipConfigured = ADDRESSES.sipRouter !== ZERO_ADDRESS

/** The legacy token id retired by an enhanced draw. Fixed by the contract. */
export const LEGACY_TOKEN_ID = 1n

/** cbBTC decimals, verified on chain. Amounts are satoshi; this is display only. */
export const CBBTC_DECIMALS = 8

export const RPC_URL = (import.meta.env.VITE_BASE_RPC_URL ?? '').trim() || undefined

/**
 * Where "[ get cbBTC ]" points. An OUTBOUND LINK ONLY — the page embeds no swap,
 * no router, no quote and no aggregator call. The participant leaves, acquires
 * cbBTC wherever they like, and comes back.
 *
 * Operator-supplied, not invented here, and overridable without a code change.
 */
export const CBBTC_LINK =
  (import.meta.env.VITE_CBBTC_LINK_URL ?? '').trim() ||
  'https://aerodrome.finance/swap?from=eth&to=0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf&chain0=8453&chain1=8453'

/**
 * ETH below this reads "low" rather than "ready".
 *
 * DISPLAY ONLY — it gates nothing and blocks no transaction; the wallet and the
 * chain remain the authority on whether gas suffices. A draw is roughly 150-250k
 * gas and Base has been running near 0.01 gwei, so 0.0002 ETH covers an approval
 * pair plus a draw with well over an order of magnitude of headroom for a spike.
 * Set deliberately loose: telling someone they are short when they are not is
 * worse than staying quiet.
 */
export const ETH_READY_WEI = 200_000_000_000_000n // 0.0002 ETH

/** Addresses that must be real before any chain interaction is allowed. */
const REQUIRED_ADDRESSES = ['faucet', 'seeds'] as const

export const missingAddresses: string[] = REQUIRED_ADDRESSES.filter(
  (key) => ADDRESSES[key] === ZERO_ADDRESS,
)

/** True only when every required address has been supplied. */
export const contractsConfigured = missingAddresses.length === 0

export const CONFIG_PENDING_MESSAGE = 'Production contract configuration pending.'
